import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "../../components/Layout";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Spinner from "react-bootstrap/Spinner";
import Modal from "react-bootstrap/Modal";
import { getProduct, addProduct, updateProduct } from "../../api/products";
import ProductPreview from "./ProductPreview";
import {
	FEATURE_ICONS,
	PRODUCT_TYPES,
	SHIPPING_CARRIERS,
	INSTANT_METHODS,
	emptyFeature,
	emptyProduct,
	productFromApi,
	slugify,
} from "./productUtils";
import "./style.css";

const MAX_IMAGE_SIZE = 2 * 1024 * 1024; // 2 MB

const ProductForm = () => {
	const { id } = useParams();
	const isEdit = Boolean(id);
	const navigate = useNavigate();
	const fileInputRef = useRef(null);

	const [product, setProduct] = useState(emptyProduct());
	const [loading, setLoading] = useState(isEdit);
	const [saving, setSaving] = useState(false);
	const [successShow, setSuccessShow] = useState(false);
	const [showPreview, setShowPreview] = useState(true);
	const [slugTouched, setSlugTouched] = useState(false);

	useEffect(() => {
		if (!isEdit) return;
		const load = async () => {
			try {
				const data = await getProduct(id);
				setProduct(productFromApi(data));
				setSlugTouched(Boolean(data.productSlug));
			} catch (error) {
				console.error("Ürün verileri getirilirken bir hata oluştu:", error);
				alert(error.message || "Ürün bulunamadı");
				navigate("/products");
			} finally {
				setLoading(false);
			}
		};
		load();
	}, [id, isEdit, navigate]);

	const setField = (field, value) =>
		setProduct((prev) => ({ ...prev, [field]: value }));
	const setShipping = (field, value) =>
		setProduct((prev) => ({ ...prev, shipping: { ...prev.shipping, [field]: value } }));
	const setInstant = (field, value) =>
		setProduct((prev) => ({
			...prev,
			instantDelivery: { ...prev.instantDelivery, [field]: value },
		}));

	const isShipping = product.productType === "shipping";

	const handleNameChange = (value) => {
		setProduct((prev) => ({
			...prev,
			productName: value,
			productSlug: slugTouched ? prev.productSlug : slugify(value),
		}));
	};

	// ---- Görsel ----
	const handleImageFile = (file) => {
		if (!file) return;
		if (!file.type.startsWith("image/")) {
			alert("Lütfen bir görsel dosyası seçiniz");
			return;
		}
		if (file.size > MAX_IMAGE_SIZE) {
			alert("Görsel boyutu en fazla 2 MB olabilir");
			return;
		}
		const reader = new FileReader();
		reader.onload = (e) => setField("productImage", e.target.result);
		reader.readAsDataURL(file);
	};

	// ---- Öne çıkan özellikler ----
	const updateFeature = (index, field, value) => {
		setProduct((prev) => {
			const features = [...prev.productFeatures];
			features[index] = { ...features[index], [field]: value };
			return { ...prev, productFeatures: features };
		});
	};
	const addFeature = () =>
		setProduct((prev) => ({
			...prev,
			productFeatures: [...prev.productFeatures, emptyFeature()],
		}));
	const removeFeature = (index) =>
		setProduct((prev) => ({
			...prev,
			productFeatures: prev.productFeatures.filter((_, i) => i !== index),
		}));
	const moveFeature = (index, direction) =>
		setProduct((prev) => {
			const features = [...prev.productFeatures];
			const target = index + direction;
			if (target < 0 || target >= features.length) return prev;
			[features[index], features[target]] = [features[target], features[index]];
			return { ...prev, productFeatures: features };
		});

	// ---- Teknik özellikler ----
	const updateSpec = (index, value) =>
		setProduct((prev) => {
			const specs = [...prev.productSpecs];
			specs[index] = value;
			return { ...prev, productSpecs: specs };
		});
	const addSpec = () =>
		setProduct((prev) => ({ ...prev, productSpecs: [...prev.productSpecs, ""] }));
	const removeSpec = (index) =>
		setProduct((prev) => ({
			...prev,
			productSpecs: prev.productSpecs.filter((_, i) => i !== index),
		}));

	// ---- Kaydet ----
	const handleSubmit = async (e) => {
		e.preventDefault();
		const sh = product.shipping;
		const toNum = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

		if (isShipping && sh.feeType !== "free" && (sh.fee === "" || Number(sh.fee) < 0)) {
			alert("Lütfen geçerli bir kargo ücreti giriniz");
			return;
		}
		if (
			isShipping &&
			Number(sh.deliveryMaxDays) < Number(sh.deliveryMinDays)
		) {
			alert("En geç teslim süresi, en erken teslim süresinden küçük olamaz");
			return;
		}

		const payload = {
			...product,
			productType: product.productType,
			shipping: isShipping
				? {
						carrier: sh.carrier,
						feeType: sh.feeType,
						fee: sh.feeType === "free" ? 0 : toNum(sh.fee),
						freeAbove: sh.feeType === "conditional" ? toNum(sh.freeAbove) : null,
						preparationDays: toNum(sh.preparationDays) ?? 0,
						deliveryMinDays: toNum(sh.deliveryMinDays) ?? 0,
						deliveryMaxDays: toNum(sh.deliveryMaxDays) ?? 0,
						weight: toNum(sh.weight),
						desi: toNum(sh.desi),
						width: toNum(sh.width),
						height: toNum(sh.height),
						depth: toNum(sh.depth),
						note: sh.note.trim(),
				  }
				: null,
			instantDelivery: !isShipping
				? {
						method: product.instantDelivery.method,
						note: product.instantDelivery.note.trim(),
				  }
				: null,
			productName: product.productName.trim(),
			productSlug: product.productSlug || slugify(product.productName),
			productPrice: Number(product.productPrice),
			productStock: product.productStock === "" ? null : Number(product.productStock),
			productFeatures: product.productFeatures
				.filter((f) => f.title.trim() || f.description.trim())
				.map((f) => ({
					icon: f.icon,
					title: f.title.trim(),
					description: f.description.trim(),
				})),
			productSpecs: product.productSpecs.map((s) => s.trim()).filter(Boolean),
		};
		delete payload._id;
		delete payload.__v;
		delete payload.createdAt;
		delete payload.updatedAt;

		setSaving(true);
		try {
			if (isEdit) {
				await updateProduct(id, payload);
			} else {
				await addProduct(payload);
			}
			setSuccessShow(true);
		} catch (error) {
			console.error("Kaydetme işlemi sırasında bir hata oluştu:", error);
			alert(
				error.message ||
					(isEdit
						? "Ürün güncellenirken bir hata oluştu"
						: "Ürün eklenirken bir hata oluştu")
			);
		} finally {
			setSaving(false);
		}
	};

	if (loading) {
		return (
			<Layout>
				<div className="user-modal-inputs" style={{ textAlign: "center", padding: "50px" }}>
					<Spinner animation="border" variant="primary" />
				</div>
			</Layout>
		);
	}

	return (
		<Layout>
			<div className={`product-form-wrapper ${showPreview ? "with-preview" : ""}`}>
				<div className="product-form-main">
					<div className="product-form-header">
						<h4>{isEdit ? "Ürünü Düzenle" : "Yeni Ürün Ekle"}</h4>
						<Form.Check
							type="switch"
							id="toggle-preview"
							label="Önizlemeyi göster"
							checked={showPreview}
							onChange={(e) => setShowPreview(e.target.checked)}
						/>
					</div>

					<Form onSubmit={handleSubmit} className="product-form">
						{/* Temel Bilgiler */}
						<section className="product-form-section">
							<h5>Temel Bilgiler</h5>
							<div className="product-form-grid">
								<Form.Group>
									<label className="mb-2">Ürün Adı *</label>
									<Form.Control
										type="text"
										placeholder="Örn: Radar Sensörü"
										value={product.productName}
										required
										onChange={(e) => handleNameChange(e.target.value)}
									/>
								</Form.Group>
								<Form.Group>
									<label className="mb-2">URL Adı (slug)</label>
									<Form.Control
										type="text"
										placeholder="radar-sensoru"
										value={product.productSlug}
										onChange={(e) => {
											setSlugTouched(true);
											setField("productSlug", slugify(e.target.value));
										}}
									/>
								</Form.Group>
								<Form.Group className="full-width">
									<label className="mb-2">Kısa Açıklama *</label>
									<Form.Control
										as="textarea"
										rows={3}
										placeholder="Ürünün ne yaptığını 1-2 cümle ile açıklayın"
										value={product.productDescription}
										required
										onChange={(e) => setField("productDescription", e.target.value)}
									/>
								</Form.Group>
							</div>
						</section>

						{/* Görsel */}
						<section className="product-form-section">
							<h5>Ürün Görseli</h5>
							<div className="product-image-editor">
								<div
									className="product-image-drop"
									onClick={() => fileInputRef.current?.click()}
									onDragOver={(e) => e.preventDefault()}
									onDrop={(e) => {
										e.preventDefault();
										handleImageFile(e.dataTransfer.files?.[0]);
									}}
								>
									{product.productImage ? (
										<img src={product.productImage} alt="Ürün görseli" />
									) : (
										<span>
											Görsel yüklemek için tıklayın
											<br />
											veya sürükleyip bırakın
										</span>
									)}
								</div>
								<input
									ref={fileInputRef}
									type="file"
									accept="image/*"
									hidden
									onChange={(e) => {
										handleImageFile(e.target.files?.[0]);
										e.target.value = "";
									}}
								/>
								<div className="product-image-controls">
									<Form.Group>
										<label className="mb-2">veya Görsel URL'si</label>
										<Form.Control
											type="text"
											placeholder="https://..."
											value={
												product.productImage?.startsWith("data:")
													? ""
													: product.productImage
											}
											onChange={(e) => setField("productImage", e.target.value)}
										/>
									</Form.Group>
									<small className="text-muted">
										Önerilen oran 3:4, en fazla 2 MB (JPG, PNG, WebP).
									</small>
									{product.productImage && (
										<Button
											variant="outline-danger"
											size="sm"
											type="button"
											onClick={() => setField("productImage", "")}
										>
											Görseli Kaldır
										</Button>
									)}
								</div>
							</div>
						</section>

						{/* Fiyat ve Stok */}
						<section className="product-form-section">
							<h5>Fiyat ve Stok</h5>
							<div className="product-form-grid three">
								<Form.Group>
									<label className="mb-2">Fiyat *</label>
									<div className="input-group">
										<Form.Control
											type="number"
											min="0"
											step="0.01"
											placeholder="15000"
											value={product.productPrice}
											required
											onChange={(e) => setField("productPrice", e.target.value)}
										/>
										<Form.Select
											value={product.productCurrency}
											onChange={(e) => setField("productCurrency", e.target.value)}
											style={{ maxWidth: "90px" }}
										>
											<option value="TL">TL</option>
											<option value="USD">USD</option>
											<option value="EUR">EUR</option>
										</Form.Select>
									</div>
								</Form.Group>
								<Form.Group>
									<label className="mb-2">Stok Adedi</label>
									<Form.Control
										type="number"
										min="0"
										placeholder="Boş bırakılırsa sınırsız"
										value={product.productStock}
										onChange={(e) => setField("productStock", e.target.value)}
									/>
								</Form.Group>
								<Form.Group>
									<label className="mb-2">Durum</label>
									<Form.Select
										value={product.productActive}
										onChange={(e) => setField("productActive", e.target.value)}
									>
										<option value="Aktif">Aktif (Satışta)</option>
										<option value="Pasif">Pasif (Gizli)</option>
									</Form.Select>
								</Form.Group>
							</div>
						</section>

						{/* Ürün / Hizmet Tipi */}
						<section className="product-form-section">
							<h5>Ürün / Hizmet Tipi</h5>
							<div className="product-type-options">
								{PRODUCT_TYPES.map((type) => (
									<label
										key={type.key}
										className={`product-type-option ${
											product.productType === type.key ? "selected" : ""
										}`}
									>
										<input
											type="radio"
											name="productType"
											value={type.key}
											checked={product.productType === type.key}
											onChange={() => setField("productType", type.key)}
										/>
										<div>
											<strong>{type.label}</strong>
											<p>{type.description}</p>
										</div>
									</label>
								))}
							</div>
						</section>

						{/* Kargolama Adımları */}
						{isShipping ? (
							<section className="product-form-section">
								<h5>Kargolama Adımları</h5>

								<div className="product-step">
									<div className="product-step-number">1</div>
									<div className="product-step-body">
										<h6>Hazırlık Süresi</h6>
										<div className="product-form-grid three">
											<Form.Group>
												<label className="mb-2">Hazırlanma süresi (iş günü) *</label>
												<Form.Control
													type="number"
													min="0"
													value={product.shipping.preparationDays}
													required
													onChange={(e) => setShipping("preparationDays", e.target.value)}
												/>
											</Form.Group>
										</div>
									</div>
								</div>

								<div className="product-step">
									<div className="product-step-number">2</div>
									<div className="product-step-body">
										<h6>Kargo Firması ve Ücreti</h6>
										<div className="product-form-grid three">
											<Form.Group>
												<label className="mb-2">Kargo firması *</label>
												<Form.Select
													value={product.shipping.carrier}
													onChange={(e) => setShipping("carrier", e.target.value)}
												>
													{SHIPPING_CARRIERS.map((c) => (
														<option value={c} key={c}>
															{c}
														</option>
													))}
												</Form.Select>
											</Form.Group>
											<Form.Group>
												<label className="mb-2">Kargo ücreti *</label>
												<Form.Select
													value={product.shipping.feeType}
													onChange={(e) => setShipping("feeType", e.target.value)}
												>
													<option value="free">Ücretsiz kargo</option>
													<option value="fixed">Sabit ücret</option>
													<option value="conditional">Belirli tutar üzeri ücretsiz</option>
												</Form.Select>
											</Form.Group>
											{product.shipping.feeType !== "free" && (
												<Form.Group>
													<label className="mb-2">
														Ücret ({product.productCurrency}) *
													</label>
													<Form.Control
														type="number"
														min="0"
														step="0.01"
														placeholder="Örn: 150"
														value={product.shipping.fee}
														required
														onChange={(e) => setShipping("fee", e.target.value)}
													/>
												</Form.Group>
											)}
											{product.shipping.feeType === "conditional" && (
												<Form.Group>
													<label className="mb-2">
														Ücretsiz kargo alt limiti ({product.productCurrency}) *
													</label>
													<Form.Control
														type="number"
														min="0"
														step="0.01"
														placeholder="Örn: 5000"
														value={product.shipping.freeAbove}
														required
														onChange={(e) => setShipping("freeAbove", e.target.value)}
													/>
												</Form.Group>
											)}
										</div>
									</div>
								</div>

								<div className="product-step">
									<div className="product-step-number">3</div>
									<div className="product-step-body">
										<h6>Teslimat Süresi</h6>
										<div className="product-form-grid three">
											<Form.Group>
												<label className="mb-2">En erken (iş günü) *</label>
												<Form.Control
													type="number"
													min="0"
													value={product.shipping.deliveryMinDays}
													required
													onChange={(e) => setShipping("deliveryMinDays", e.target.value)}
												/>
											</Form.Group>
											<Form.Group>
												<label className="mb-2">En geç (iş günü) *</label>
												<Form.Control
													type="number"
													min="0"
													value={product.shipping.deliveryMaxDays}
													required
													onChange={(e) => setShipping("deliveryMaxDays", e.target.value)}
												/>
											</Form.Group>
										</div>
										<small className="text-muted">
											Müşteriye gösterilecek süre hazırlık süresi ile toplanır.
										</small>
									</div>
								</div>

								<div className="product-step">
									<div className="product-step-number">4</div>
									<div className="product-step-body">
										<h6>Paket Bilgileri</h6>
										<div className="product-form-grid three">
											<Form.Group>
												<label className="mb-2">Ağırlık (kg)</label>
												<Form.Control
													type="number"
													min="0"
													step="0.01"
													value={product.shipping.weight}
													onChange={(e) => setShipping("weight", e.target.value)}
												/>
											</Form.Group>
											<Form.Group>
												<label className="mb-2">Desi</label>
												<Form.Control
													type="number"
													min="0"
													step="0.1"
													value={product.shipping.desi}
													onChange={(e) => setShipping("desi", e.target.value)}
												/>
											</Form.Group>
											<Form.Group>
												<label className="mb-2">Boyutlar (cm) En × Boy × Derinlik</label>
												<div className="product-dimensions">
													<Form.Control
														type="number"
														min="0"
														placeholder="En"
														value={product.shipping.width}
														onChange={(e) => setShipping("width", e.target.value)}
													/>
													<span>×</span>
													<Form.Control
														type="number"
														min="0"
														placeholder="Boy"
														value={product.shipping.height}
														onChange={(e) => setShipping("height", e.target.value)}
													/>
													<span>×</span>
													<Form.Control
														type="number"
														min="0"
														placeholder="Derinlik"
														value={product.shipping.depth}
														onChange={(e) => setShipping("depth", e.target.value)}
													/>
												</div>
											</Form.Group>
										</div>
									</div>
								</div>

								<div className="product-step">
									<div className="product-step-number">5</div>
									<div className="product-step-body">
										<h6>Kargo Notu</h6>
										<Form.Group>
											<Form.Control
												as="textarea"
												rows={2}
												placeholder="Müşteriye gösterilecek not (örn: Kırılabilir ürün, özel paketlemeyle gönderilir.)"
												value={product.shipping.note}
												onChange={(e) => setShipping("note", e.target.value)}
											/>
										</Form.Group>
									</div>
								</div>
							</section>
						) : (
							<section className="product-form-section">
								<h5>Anlık Teslim Ayarları</h5>
								<div className="product-form-grid">
									<Form.Group>
										<label className="mb-2">Teslim yöntemi *</label>
										<Form.Select
											value={product.instantDelivery.method}
											onChange={(e) => setInstant("method", e.target.value)}
										>
											{INSTANT_METHODS.map((m) => (
												<option value={m.key} key={m.key}>
													{m.label}
												</option>
											))}
										</Form.Select>
									</Form.Group>
									<Form.Group className="full-width">
										<label className="mb-2">Teslim notu</label>
										<Form.Control
											as="textarea"
											rows={2}
											placeholder="Müşteriye gösterilecek not (örn: Ödeme sonrası aktivasyon bilgileri e-posta adresinize gönderilir.)"
											value={product.instantDelivery.note}
											onChange={(e) => setInstant("note", e.target.value)}
										/>
									</Form.Group>
								</div>
								<small className="text-muted">
									Bu tip için kargo süreci uygulanmaz, sipariş ödeme sonrası tamamlanmış sayılır.
								</small>
							</section>
						)}

						{/* Öne Çıkan Özellikler */}
						<section className="product-form-section">
							<div className="product-form-section-header">
								<h5>Öne Çıkan Özellikler</h5>
								<Button variant="outline-primary" size="sm" type="button" onClick={addFeature}>
									+ Özellik Ekle
								</Button>
							</div>
							{product.productFeatures.length === 0 && (
								<p className="text-muted">Henüz özellik eklenmedi.</p>
							)}
							{product.productFeatures.map((feature, index) => (
								<div className="product-feature-row" key={index}>
									<Form.Select
										value={feature.icon}
										onChange={(e) => updateFeature(index, "icon", e.target.value)}
										className="product-feature-icon"
									>
										{FEATURE_ICONS.map((icon) => (
											<option value={icon.key} key={icon.key}>
												{icon.label}
											</option>
										))}
									</Form.Select>
									<Form.Control
										type="text"
										placeholder="Başlık (örn: Düşme Sensörü)"
										value={feature.title}
										onChange={(e) => updateFeature(index, "title", e.target.value)}
									/>
									<Form.Control
										type="text"
										placeholder="Açıklama (örn: Ani düşüşleri algılar)"
										value={feature.description}
										onChange={(e) => updateFeature(index, "description", e.target.value)}
									/>
									<div className="product-row-actions">
										<Button
											variant="light"
											size="sm"
											type="button"
											disabled={index === 0}
											onClick={() => moveFeature(index, -1)}
											title="Yukarı taşı"
										>
											↑
										</Button>
										<Button
											variant="light"
											size="sm"
											type="button"
											disabled={index === product.productFeatures.length - 1}
											onClick={() => moveFeature(index, 1)}
											title="Aşağı taşı"
										>
											↓
										</Button>
										<Button
											variant="danger"
											size="sm"
											type="button"
											onClick={() => removeFeature(index)}
										>
											Sil
										</Button>
									</div>
								</div>
							))}
						</section>

						{/* Teknik Özellikler */}
						<section className="product-form-section">
							<div className="product-form-section-header">
								<h5>Teknik Özellikler</h5>
								<Button variant="outline-primary" size="sm" type="button" onClick={addSpec}>
									+ Madde Ekle
								</Button>
							</div>
							{product.productSpecs.length === 0 && (
								<p className="text-muted">Henüz teknik özellik eklenmedi.</p>
							)}
							{product.productSpecs.map((spec, index) => (
								<div className="product-spec-row" key={index}>
									<Form.Control
										type="text"
										placeholder="Örn: 360° Hareket Algılama: Odadaki tüm hareketleri kapsar."
										value={spec}
										onChange={(e) => updateSpec(index, e.target.value)}
									/>
									<Button
										variant="danger"
										size="sm"
										type="button"
										onClick={() => removeSpec(index)}
									>
										Sil
									</Button>
								</div>
							))}
						</section>

						<div className="button-wrapper">
							<Button
								variant="secondary"
								type="button"
								onClick={() => navigate("/products")}
							>
								İptal
							</Button>
							<Button variant="primary" type="submit" disabled={saving}>
								{saving ? (
									<>
										<Spinner animation="border" size="sm" style={{ marginRight: "8px" }} />
										Kaydediliyor...
									</>
								) : isEdit ? (
									"Güncelle"
								) : (
									"Ürünü Ekle"
								)}
							</Button>
						</div>
					</Form>
				</div>

				{showPreview && (
					<aside className="product-form-preview">
						<h6>Önizleme</h6>
						<ProductPreview product={product} />
					</aside>
				)}
			</div>

			<Modal show={successShow} onHide={() => navigate("/products")}>
				<Modal.Header closeButton></Modal.Header>
				<Modal.Body>
					{isEdit ? "Ürün başarıyla güncellendi." : "Ürün başarıyla eklendi."}
				</Modal.Body>
				<Modal.Footer>
					<Button variant="primary" onClick={() => navigate("/products")}>
						Ürün Listesine Dön
					</Button>
				</Modal.Footer>
			</Modal>
		</Layout>
	);
};

export default ProductForm;
